import Box from '@mui/system/Box';
import React, { useState, useMemo, useRef } from 'react';
import {
  Chip,
  colors,
  Divider,
  Dialog,
  FormControl,
  FormLabel,
  FormControlLabel,
  IconButton,
  RadioGroup,
  Radio,
  Stack,
  Typography,
  List,
  ListItem,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { FileUploadOutlined, Close, AttachFile, Check, Report, People } from '@mui/icons-material';
import { CSVLink } from 'react-csv';

import { UploadPaylinkCustomersBody } from '../../../../store/modules/merchant/types';
import { Alert } from '../Alert';
import { colorSuccess, colorError, colorWarning } from '../../../../util/constants';
import { toast } from '../../../../util/toast';
import { Button } from '../../../../components/core/Button/Button';

const UPLOAD_CUSTOMERS_TEMPLATE =
  'First Name,Last Name,Phone,Email\nJohn,Doe,1234567890,johndoe@gmail.com\n';

interface Props {
  onSubmit: (customer: UploadPaylinkCustomersBody) => Promise<any>;
  isOpen: boolean;
  onClose: () => void;
  serverValidationError?: string;
}

const baseDropzoneStyle = {
  borderWidth: '1px',
  borderRadius: '4px',
  borderStyle: 'solid',
  borderColor: 'border.main',
  padding: '24px',
  width: '100%',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
} as const;

const acceptStyle = {
  borderColor: 'success.main',
};

const rejectStyle = {
  borderColor: 'error.main',
};

// 500KB
const MAX_FILE_SIZE = 500000;

/**
 * @summary Modal component to bulk upload customers
 * @param {Props} props Component properties
 * @returns {JSX.Element} upload customers modal
 */
const UploadCustomersModal = ({ isOpen, onSubmit, onClose, serverValidationError }: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [errorFile, setErrorFile] = useState<string | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [shouldOverwrite, setShouldOverwrite] = useState<boolean>(false);
  const [errorCount, setErrorCount] = useState<number | undefined>();
  const [successCount, setSuccessCount] = useState<number | undefined>();
  const [duplicateCount, setDuplicateCount] = useState<number | undefined>();

  const onSubmitWithEffects = async (customerInfo: UploadPaylinkCustomersBody) => {
    setErrorFile(undefined);
    setErrorCount(undefined);
    setSuccessCount(undefined);
    setDuplicateCount(undefined);
    if (customerInfo.file.size > MAX_FILE_SIZE) {
      setErrorMessage(
        'Your file exceeds the accepted file size of 500KB. Please try again with a smaller file.',
      );
      return;
    }
    setIsProcessing(true);
    let res;
    try {
      res = await onSubmit(customerInfo);
    } finally {
      setIsProcessing(false);
    }
    if (res.file) {
      setErrorFile(res.file);
      setErrorCount(res.totalErrors);
      setSuccessCount(res.totalUploaded);
      setDuplicateCount(res.totalDuplicates);
    } else {
      toast.success('New customers from file were successfully uploaded.');
      onClose();
      setFile(undefined);
    }
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    onDrop: (files) => {
      setErrorMessage(undefined);
      if (!files.length) {
        setErrorMessage('Please upload a valid .csv file.');
      }
      setFile(files[0]);
    },
  });

  const dropzoneStyle = useMemo(
    () => ({
      ...baseDropzoneStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragReject],
  );

  const csvLink = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>();
  const templateCsvLink = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>();

  const fileMarkup = (
    <Box>
      <div {...getRootProps({ style: dropzoneStyle })}>
        Attached file
        <Chip
          sx={{
            bgcolor: colors.grey[300],
            height: '26px',
            borderRadius: '50px',

            fontSize: 14,
            color: colors.common.black,
            '& .MuiChip-deleteIcon': {
              color: 'link.main',
              fontSize: '16px',
            },
          }}
          deleteIcon={<Close />}
          label={file?.name}
          onDelete={() => {
            setFile(undefined);
            setErrorMessage(undefined);
          }}
          icon={<AttachFile sx={{ fontSize: '16px' }} />}
        />
        <input {...getInputProps()} />
        <Stack direction="row" spacing={0.75} sx={{ '& p': { fontSize: '14px' } }}>
          <Typography
            sx={{
              color: 'link.main',
              fontWeight: 700,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Click to replace
          </Typography>
          <Typography>or drag and drop file</Typography>
        </Stack>
      </div>
      <Box sx={{ mt: '16px' }}>
        <FormControl>
          <FormLabel
            id="keep-or-update-group-label"
            sx={{
              '&, &.Mui-focused': {
                fontSize: 14,
                color: 'rgba(0, 0, 0, 0.87)',
              },
            }}
          >
            If customers you are uploading already exist, do you want to:
          </FormLabel>
          <RadioGroup
            name="keep-or-update"
            defaultValue="keep"
            aria-labelledby="keep-or-update-group-label"
          >
            <FormControlLabel
              value="keep"
              control={
                <Radio
                  sx={{
                    py: '5px',
                    '&, &.Mui-checked': {
                      color: 'text.primary',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px' }}>
                  Keep their current customer information
                </Typography>
              }
            />
            <FormControlLabel
              value="update"
              control={
                <Radio
                  sx={{
                    py: '5px',
                    '&, &.Mui-checked': {
                      color: 'text.primary',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px' }}>Update their customer information</Typography>
              }
              onChange={(e) => setShouldOverwrite((e.target as HTMLInputElement).checked)}
            />
          </RadioGroup>
        </FormControl>
      </Box>
    </Box>
  );

  const noFileMarkup = (
    <div {...getRootProps({ style: dropzoneStyle })}>
      <IconButton
        aria-label="upload"
        sx={{
          px: 1,
          backgroundColor: 'secondary.main',
          color: 'text.primary',
          py: 1,
          borderRadius: '100px',
          border: '7px solid',
          borderColor: 'primary.light',
          width: '56px',
          marginBottom: '8px',
        }}
      >
        <FileUploadOutlined />
      </IconButton>
      <input {...getInputProps()} data-testid="file-drop-target" />
      <Stack direction="row" spacing={0.75} sx={{ '& p': { fontSize: '14px' } }}>
        <Typography
          sx={{
            color: 'link.main',
            fontWeight: 700,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Click to upload
        </Typography>
        <Typography>or drag and drop file</Typography>
      </Stack>
    </div>
  );

  const errorFileMarkup = (
    <Stack spacing={1} sx={{ '& p': { fontSize: '14px' } }}>
      The following was found while uploading your file:
      <Stack
        direction="row"
        sx={{ alignItems: 'center', color: colorSuccess.text, pt: '8px' }}
        spacing={1}
      >
        <Check sx={{ fontSize: '18px' }} />
        <Typography>{successCount} new Customers successfully uploaded</Typography>
      </Stack>
      {!!duplicateCount && (
        <Stack direction="row" sx={{ alignItems: 'center', color: colorWarning.text }} spacing={1}>
          <People sx={{ fontSize: '18px' }} />
          <Typography>{duplicateCount} Customers from file already exist</Typography>
        </Stack>
      )}
      <Stack direction="row" sx={{ alignItems: 'center', color: colorError.text }} spacing={1}>
        <Report sx={{ fontSize: '18px' }} />
        <Typography>
          {errorCount} new Customers with errors. Please download the following file to fix the
          displayed errors and try uploading it again.
        </Typography>
      </Stack>
      <Typography sx={{ pt: '23px' }}>Download file</Typography>
      <Chip
        sx={{
          bgcolor: colors.grey[300],
          height: '26px',
          width: 'fit-content',
          borderRadius: '50px',

          fontSize: 14,
          fontWeight: 700,
          color: 'link.main',
          '& .MuiChip-icon': {
            color: 'link.main',
          },
        }}
        label={`${file?.name.split('.')[0]}_Errors.csv`}
        icon={<AttachFile sx={{ fontSize: '16px' }} />}
        onClick={() => csvLink.current && csvLink.current.link.click()}
      />
      <CSVLink
        data={errorFile || ''}
        filename={`${file?.name.split('.')[0]}_Errors.csv`}
        className="hidden"
        ref={csvLink}
        target="_blank"
      />
    </Stack>
  );

  return (
    <Dialog open={isOpen} onClose={onClose} PaperProps={{ sx: { borderRadius: 4, width: 560 } }}>
      <Stack
        sx={{
          padding: (theme) => theme.spacing(2, 4, 3),
          gap: '20px',
        }}
        justifyContent="center"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="xl" fontWeight="semibold">
              Upload Customers
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close sx={{ color: 'text.primary' }} />
          </IconButton>
        </Box>
        {[errorMessage, serverValidationError].map(
          (e) =>
            e && (
              <Alert key={e} severity="error" sx={{ mb: '5px' }}>
                {e}
              </Alert>
            ),
        )}

        {errorFile ? (
          errorFileMarkup
        ) : (
          <>
            <Box sx={{ '& p': { fontSize: 14 }, mb: 2 }}>
              <Typography>To bulk upload customers, please follow these instructions:</Typography>
              <List
                sx={{
                  listStyle: 'decimal',
                  p: (t) => t.spacing(0.5, 0, 0, 2),
                  '& li': {
                    display: 'list-item',
                    m: 0,
                    p: 0,
                  },
                }}
              >
                <ListItem
                  sx={{
                    '& button': {
                      p: (theme) => theme.spacing(0, 0, 0.25),
                      '& p': {
                        color: 'link.main',
                        textDecoration: 'underline',
                      },
                    },
                  }}
                >
                  <Button
                    variant="text"
                    text="Download"
                    onClick={() => templateCsvLink.current?.link.click()}
                  />{' '}
                  this template for file formatting guidance
                </ListItem>
                <ListItem>
                  Fill in your spreadsheet ensuring every row in the file has: <br />
                  First name, last name, and either phone or email
                </ListItem>
                <ListItem>Upload a .csv file with less than 1,000 rows below</ListItem>
              </List>
              <CSVLink
                data={UPLOAD_CUSTOMERS_TEMPLATE}
                filename="upload_customers_template.csv"
                className="hidden"
                ref={templateCsvLink}
                target="_blank"
              />
            </Box>
            {file ? fileMarkup : noFileMarkup}
          </>
        )}

        <Divider sx={{ width: '100%' }} />
        <Stack
          direction="row"
          spacing={3}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            '& button': {
              width: '100%',
              height: '48px',
              fontWeight: 600,

              fontSize: 14,
              letterSpacing: 'initial',
              borderRadius: '50px',
              textTransform: 'none',
            },
          }}
        >
          <Button
            onClick={() => {
              setFile(undefined);
              setErrorMessage(undefined);
              setErrorFile(undefined);
              setErrorCount(undefined);
              setSuccessCount(undefined);
              setDuplicateCount(undefined);
              onClose();
            }}
            theme="secondary"
            text="Cancel"
          />
          {errorFile ? (
            <Button
              onClick={() => {
                setFile(undefined);
                setErrorFile(undefined);
                setErrorCount(undefined);
                setSuccessCount(undefined);
                setDuplicateCount(undefined);
              }}
              text="Try again"
            />
          ) : (
            <Button
              disabled={isProcessing || !file}
              onClick={() =>
                file &&
                onSubmitWithEffects({
                  shouldOverwrite,
                  file,
                })
              }
              text="Upload"
            />
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default UploadCustomersModal;
